import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useRouter } from 'next/router';
import { FlyToInterpolator } from 'react-map-gl';
import cn from 'classnames';
import { get, keys } from 'lib/utils/localStorage';
import { reformatLocaleToContentfulLocale } from 'lib/utils/helpers';
import useIsMobile from 'hooks/utils/useIsMobile';
import { db, ensureServerAuth } from 'lib/firebase';
import useVisitorTypeTracking from 'hooks/tracking/useVisitorTypeTracking';
import {
  handleSalonsInTwentyKmRadius,
  handleSalonsInFiftyKmRadius,
  handleSalonsInOneHundredKmRadius,
  salonMatchesEmail,
} from 'lib/utils/salonsHelpers';
import Layout from 'components/layout';
import Map from 'components/pages/salons/map';
import MatchesList from 'components/pages/salons/matchesList';
import SalonDetails from 'components/pages/salons/salonDetails';
import SearchBar from 'components/pages/salons/searchBar';
import Filter from 'components/pages/salons/searchFilter';
import Icon from 'components/shared/icon';
import styles from 'components/pages/salons/salons.module.scss';
import { getGlobal } from 'lib/contentful';
import { useSettings } from 'contexts/SettingContext';
import Head from 'next/head';
import { getSelectedSalonNo, getStorageCustomerSalonName } from 'lib/shopify';
import { useGlobalModules } from 'contexts';

export default function FindASalon({ salonData, locale }) {
  const globalModules = useGlobalModules();
  const extractedCountries = Object.values(globalModules?.countrySelectorLocales);
  const allLocales = extractedCountries
    .map(country => country.countryList)
    .flat()
    .filter(Boolean);

  const resultsLimit = parseInt(globalModules.translations?.salonFinder?.resultsLimit, 10);

  const defaultLanguage = allLocales.find(
    singleLocale => singleLocale?.slug === `/${locale}/`
  )?.defaultLanguage;

  const rewriteLocales = allLocales.map(rLocale => {
    if (rLocale.slug) {
      return {
        ...rLocale,
        slug: rLocale?.slug.slice(1, rLocale.slug.length - 1),
      };
    }

    return rLocale;
  });

  const relatedCountries = rewriteLocales.find(
    singleLocale => singleLocale?.slug === locale
  )?.relatedCountries;

  const returnQueryString = countryArray => {
    let queryString = '';
    for (let i = 0; i < countryArray?.length; i++) {
      queryString += `|country:${countryArray[i].toLowerCase()}`;
    }
    return queryString;
  };
  const localeQuery = returnQueryString(relatedCountries);

  const {
    keuneAmbassadorSalonContent,
    pageTitle,
    searchBarPlaceholder,
    searchFiltersPointer,
    searchFilterDescription,
    javascriptDisabledMessage,
    searchFilters: filtersList,
    defaultSelectedFilters: defaultFilters,
    myLocation,
    noLocation,
    useLocation,
  } = globalModules?.translations?.salonFinder || {};

  const formattedDefaultFilters = useMemo(() => {
    if (!defaultFilters || !defaultFilters.length) return [filtersList[0].toUpperCase()];

    const existingFilters = filtersList.map(f => f.toUpperCase());

    const result = defaultFilters
      .map(f => f.toUpperCase())
      .filter(f => existingFilters.includes(f));

    if (!result.length) return [filtersList[0].toUpperCase()];
    return result;
  }, [defaultFilters, filtersList]);

  const isAllSalonsFilterDefault = formattedDefaultFilters.includes(filtersList[0]?.toUpperCase());

  const matches = useRef(null);

  const { salons, setSalons } = useSettings();
  const [userCountryCode, setUserCountryCode] = useState('');
  const [disableSearch, setDisableSearch] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [active, setActive] = useState(filtersList[0].toUpperCase());
  const [activeFilters, setActiveFilters] = useState(formattedDefaultFilters);
  const [salonsInRadius, setSalonsInRadius] = useState(() => {
    if (formattedDefaultFilters.includes(filtersList[0]?.toUpperCase())) {
      return salonData;
    }
    return salonData.filter(salon => {
      if (formattedDefaultFilters.includes(filtersList[1]?.toUpperCase()) && salon.salonLoyalty === true) {
        return true;
      }
      if (formattedDefaultFilters.includes(filtersList[2]?.toUpperCase()) && salon.client100 === true) {
        return true;
      }
      return false;
    });
  });
  const [selectedSalon, setSelectedSalon] = useState({});
  const [salonFocus, setSalonFocus] = useState({});
  const [showSalon, setShowSalon] = useState(false);
  const [offset, setOffset] = useState(0);
  const [headerOffset, setHeaderOffset] = useState(0);

  const [initialViewport, setInitialViewport] = useState({ longitude: 0, latitude: 0 });
  const [viewport, setViewport] = useState({
    width: '100%',
    height: '100%',
    longitude: 0,
    latitude: 0,
    zoom: 6.5,
  });
  const [gaveGeoPermission, setGaveGeoPermission] = useState({ state: 'prompt' });
  const [hoverPin, setHoverPin] = useState(null);
  const [hovered, setHovered] = useState(null);
  const isMobile = useIsMobile();
  const [showMap, setShowMap] = useState(false);
  const router = useRouter();
  const customerEmail = get(keys.LOGGED_IN_CUSTOMER);

  const filteredAllSalons = useMemo(() => {
    if (activeFilters.includes(filtersList[0]?.toUpperCase())) {
      return salons;
    }
    return salons.filter(salon => {
      if (activeFilters.includes(filtersList[1]?.toUpperCase()) && salon.salonLoyalty === true) {
        return true;
      }
      if (activeFilters.includes(filtersList[2]?.toUpperCase()) && salon.client100 === true) {
        return true;
      }
      return false;
    });
  }, [salons, activeFilters, filtersList]);

  const scrollUp = () => {
    window.scrollTo({ top: `${offset + headerOffset + 40}`, left: 0, behavior: 'smooth' });
  };

  const setGeolocation = (lat, lon) => {
    if (!gaveGeoPermission) setGaveGeoPermission(true);
    setViewport({ ...viewport, latitude: lat, longitude: lon, zoom: 14 });
    setInitialViewport({ ...viewport, latitude: lat, longitude: lon, zoom: 14 });

    setSearchText(myLocation);
    setSalons(salonData);
    if (loading) setLoading(false);
  };

  const fetchLocationThroughIP = () => {
    fetch('/api/geolocation')
      .then(res => res.json())
      .then(location => {
        const renderPage = () => {
          setUserCountryCode(location.countryCode);
          setViewport({
            ...viewport,
            latitude: location.lat,
            longitude: location.lon,
          });
          setInitialViewport({ latitude: location.lat, longitude: location.lon });
          setSalons(salonData);
          setLoading(false);
        };
        renderPage();
      })
      .catch(err => console.error(err));
  };

  const getGeolocation = () => {
    // Try to get location by the Browser API
    navigator.geolocation.getCurrentPosition(
      position => setGeolocation(position.coords.latitude, position.coords.longitude),
      () => {
        // If the browser API geolocation fails, use the IP geolocation
        fetchLocationThroughIP();
      }
    );
  };

  const decideLocationButtonText = () => {
    if (gaveGeoPermission.state === 'denied') return noLocation;
    return useLocation;
  };

  const getMobileMapButtonText = () => {
    if (showMap) return globalModules?.translations?.salonFinder?.hideMap || 'Hide map';
    return globalModules?.translations?.salonFinder?.showMap || 'Show map';
  };

  const handleZoomIn = (lat, lon) => {
    setViewport({
      ...viewport,
      longitude: lon,
      latitude: lat,
      zoom: 16,
      transitionDuration: 1000,
      transitionInterpolator: new FlyToInterpolator(),
    });
  };

  const handleSalonFocus = (salon, index) => {
    setShowSalon(true);
    setSalonFocus(salon);
    setHovered(index);
    handleZoomIn(Number(salon?.position?.latitude), Number(salon?.position?.longitude));

    if (window && window.innerWidth < 900) {
      scrollUp();
    }
  };

  const handleSalonsInRadius = salons => {
    const getPosition = salon => ({
      latitude: salon?.position?.latitude,
      longitude: salon?.position?.longitude,
    });

    const salonsInTwentyKmRadius = salons.filter(salon =>
      handleSalonsInTwentyKmRadius(getPosition(salon), viewport)
    );
    if (salonsInTwentyKmRadius.length > 0) return salonsInTwentyKmRadius;

    const salonsInFiftyKmRadius = salons.filter(salon =>
      handleSalonsInFiftyKmRadius(getPosition(salon), viewport)
    );
    if (salonsInFiftyKmRadius.length > 0) return salonsInFiftyKmRadius;

    const salonsInOneHundredKmRadius = salons.filter(salon =>
      handleSalonsInOneHundredKmRadius(getPosition(salon), viewport)
    );
    if (salonsInOneHundredKmRadius.length > 0) return salonsInOneHundredKmRadius;

    return [];
  };

  const handleSearchClick = async input => {
    if (typeof input !== 'object') {
      const cleanedInput = input.replace('&', 'and').replace(/[^a-zA-Z0-9 ]/g, '') || '';
      const filtered = salons.filter(salon => {
        const { salonName, AddressLine2, zipcode, city, country } = salon;
        const values = [salonName, AddressLine2, zipcode, city, country];
        const loweredSearchText = searchText.toLowerCase();
        return values.find(
          value => value != null && value.toLowerCase().includes(loweredSearchText)
        );
      });

      const getQuerry = () => {
        if (filtered.length !== 1) return cleanedInput;
        const q = [
          filtered[0].AddressLine2 || '',
          filtered[0].country || '',
          filtered[0].ZipCode || filtered[0].zipCode || '',
          filtered[0].city || '',
          filtered[0].salonName || '',
        ].join(',');
        return q;
      };

      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${getQuerry()}&key=${
          process.env.NEXT_PUBLIC_GOOGLE_API_KEY
        }`
      );
      const data = await response.json();

      const updatePage = async () => {
        const longitude = data.results[0].geometry.location.lng;
        const latitude = data.results[0].geometry.location.lat;

        setViewport({
          ...viewport,
          longitude,
          latitude,
          zoom: 12,

          transitionDuration: 1400,
          transitionInterpolator: new FlyToInterpolator(),
        });
        setInitialViewport({ latitude, longitude });
      };

      if (data.results.length === 1 && filtered.length !== 1) {
        updatePage();
    
      } else if (data.results.length && filtered.length === 1) {
        const { longitude, latitude } = filtered[0].position;
        setViewport({
          ...viewport,
          longitude,
          latitude,
          transitionDuration: 1400,
          zoom: 12,
          transitionInterpolator: new FlyToInterpolator(),
        });
        setInitialViewport({ latitude, longitude });

    
      } else {
        const { longitude, latitude } = initialViewport;
        setViewport({
          ...viewport,
          latitude,
          longitude,
        });
        setInitialViewport({ latitude, longitude });
    
      }

      if (isMobile && matches.current) {
        window.scrollTo(0, matches.current.offsetTop - 160);
      }
    }
  };

  const handleEnterKeyPress = e => {
    if (searchText?.length && e.key === 'Enter') {
      handleSearchClick(e.currentTarget.value);
      setShowSuggestions(false);
    }
  };

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      const devCoordinates = { latitude: 52.3782, longitude: 4.853 };
      setUserCountryCode('NL');
      setViewport({
        ...viewport,
        ...devCoordinates,
      });
      setInitialViewport(devCoordinates);
      setSalons(salonData);
      setLoading(false);
    } else {
      getGeolocation();
    }
  }, []);

  useEffect(() => {
    const checkSalonNo = getSelectedSalonNo(locale);
    const checkSalonName = getStorageCustomerSalonName(locale);

    if (checkSalonNo && checkSalonName) {
      setSelectedSalon({ salonNo: checkSalonNo, salonName: checkSalonName });
    }
  }, []);

  const filterSalons = () => {
    if (!salons.length) return;
    const inRadius = handleSalonsInRadius(salons);
    if (activeFilters.includes(filtersList[0]?.toUpperCase())) {
      setSalonsInRadius(inRadius);
    } else {
      const filteredSalons = inRadius.filter(salon => {
        const isAmbasasdor = salon.salonLoyalty === true;
        const isKeune = salon.client100 === true;
        if (activeFilters.includes(filtersList[1]?.toUpperCase()) && isAmbasasdor) {
          return true;
        }
        if (activeFilters.includes(filtersList[2]?.toUpperCase()) && isKeune) {
          return true;
        }
        return false;
      });

      setSalonsInRadius(filteredSalons);
    }
  };

  // Immediate filter when user changes filters or salons load
  useEffect(() => {
    filterSalons();
  }, [activeFilters, salons]);

  // Debounced filter on viewport changes (avoids lag during zoom/pan)
  useEffect(() => {
    const timeout = setTimeout(() => {
      filterSalons();
    }, 300);
    return () => clearTimeout(timeout);
  }, [viewport]);

  useEffect(() => {
    if (router.query.salonNo) {
      const favouriteSalon = salons.find(salon => salon.salonNo === router.query.salonNo);

      if (favouriteSalon) {
        setSalonFocus(favouriteSalon);
        setShowSalon(true);
      }
    }
  }, [salons]);

  useEffect(() => {
    if (customerEmail) {
      const fetchedSalons = salons.filter(s => salonMatchesEmail(s, customerEmail));
      if (fetchedSalons.length > 0) {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useVisitorTypeTracking({
          visitorType: fetchedSalons[0].salonNo,
          location: { action: router },
        });
      }
      // eslint-disable-next-line react-hooks/rules-of-hooks
      else useVisitorTypeTracking({ visitorType: 'KEUNE Cosmetics', location: { action: router } });
    }
    // eslint-disable-next-line react-hooks/rules-of-hooks
    else useVisitorTypeTracking({ visitorType: 'KEUNE Cosmetics', location: { action: router } });
  }, [router]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [showSalon]);

  const renderFilters = (className) => (
    <div className={className || styles.filtersContainer}>
      <div className={styles.filtersLabel}>
        <span className={styles.filtersPointer}>{searchFiltersPointer}</span>
        {!isAllSalonsFilterDefault && (
          <span className={styles.filtersDescription}>
            {searchFilterDescription}
          </span>
        )}
      </div>
      <div className={styles.filtersButtons}>
        {filtersList.map(name => (
          <Filter
            key={name}
            content={name.toUpperCase()}
            filtersList={filtersList}
            activeFilters={activeFilters}
            setActiveFilters={setActiveFilters}
          />
        ))}
      </div>
    </div>
  );

  return (
    <Layout locale={locale} globalModules={globalModules}>
      <>
        <Head>
          <title>{pageTitle} | Keune Haircosmetics</title>
        </Head>
        <noscript>
          <div className={styles.noScriptMessage}>{javascriptDisabledMessage}</div>
        </noscript>
        {loading && salons.length === 0 && (
          <div className={styles.loading}>
            <Icon type="loading" className={styles.loader} />
          </div>
        )}
        {!loading && salons.length && (
          <div className={styles.findSalons}>
            {showSalon && (
              <div className={styles.salonsContainer}>
                <SalonDetails
                  salon={salonFocus}
                  active={active}
                  locale={locale}
                  hoverPin={hoverPin}
                  closedText="Closed"
                  languageCode="en"
                  activeFilters={activeFilters}
                  selectedSalon={selectedSalon}
                  // shopNowButton={shopNowButton}
                  // shopNowContent={shopNowContent}
                  salonsInRadius={salonsInRadius}
                  initialViewport={initialViewport}
                  keuneAmbassadorSalonContent={keuneAmbassadorSalonContent}
                  setHovered={setHovered}
                  setShowSalon={setShowSalon}
                  setSearchText={setSearchText}
                  setSalonFocus={setSalonFocus}
                  setSelectedSalon={setSelectedSalon}
                  setActiveFilters={setActiveFilters}
                  setDisableSearch={setDisableSearch}
                  setSalonsInRadius={setSalonsInRadius}
                  setShowSuggestions={setShowSuggestions}
                  defaultLanguage={defaultLanguage}
                />
              </div>
            )}

            {!showSalon && (
              <div className={cn(styles.salonsContainer, !showSalon && styles.addWidth)}>
                <div className={styles.isSticky}>
                  <h1 className={cn(styles.title, { [styles.srOnly]: isMobile })}>{pageTitle}</h1>
                  <SearchBar
                    viewport={viewport}
                    searchText={searchText}
                    activeFilters={activeFilters}
                    disableSearch={disableSearch}
                    initialViewport={initialViewport}
                    showSuggestions={showSuggestions}
                    userCountryCode={userCountryCode}
                    localeQuery={localeQuery}
                    selectedSuggestion={selectedSuggestion}
                    searchBarPlaceholder={searchBarPlaceholder}
                    handleSearchClick={handleSearchClick}
                    handleEnterKeyPress={handleEnterKeyPress}
                    setDisableSearch={setDisableSearch}
                    setViewport={setViewport}
                    setSearchText={setSearchText}
                    setActiveFilters={setActiveFilters}

                    setShowSuggestions={setShowSuggestions}
                    setSelectedSuggestion={setSelectedSuggestion}
                  />
                  {isMobile ? (
                    <div className={styles.locationButtons}>
                      <button
                        type="button"
                        className={styles.useLocationButton}
                        onClick={() => setShowMap(!showMap)}
                      >
                        {getMobileMapButtonText()}
                      </button>
                      <button
                        type="button"
                        className={styles.useLocationButton}
                        disabled={gaveGeoPermission.state === 'denied'}
                        onClick={getGeolocation}
                      >
                        {decideLocationButtonText()}
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className={styles.useLocationButton}
                      disabled={gaveGeoPermission.state === 'denied'}
                      onClick={getGeolocation}
                      onKeyDown={getGeolocation}
                    >
                      {decideLocationButtonText()}
                    </button>
                  )}
                  {filtersList && !isMobile && renderFilters()}
                </div>
                {isMobile && showMap && (
                  <Map
                    loading={loading}
                    hovered={hovered}
                    viewport={viewport}
                    allSalons={filteredAllSalons}
                    salonsInRadius={salonsInRadius}
                    initialViewport={initialViewport}
                    setInitialViewport={setInitialViewport}
                    setHovered={setHovered}
                    setViewport={setViewport}
                    handleSalonFocus={handleSalonFocus}
                  />
                )}
                {isMobile && filtersList && renderFilters(
                  cn(styles.filtersContainer, showMap && styles.withMapMargin)
                )}
                {salonsInRadius && (
                  <div ref={matches}>
                    <MatchesList
                      hovered={hovered}
                      salons={salons}
                      salonsInRadius={salonsInRadius}
                      initialViewport={initialViewport}
                      resultsLimit={resultsLimit}
                      setActive={setActive}
                      setHovered={setHovered}
                      setShowSalon={setShowSalon}
                      setSalonFocus={setSalonFocus}
                    />
                  </div>
                )}
              </div>
            )}
            {!showSalon && !isMobile && salonsInRadius && (
              <Map
                loading={loading}
                hovered={hovered}
                viewport={viewport}
                allSalons={filteredAllSalons}
                salonsInRadius={salonsInRadius}
                initialViewport={initialViewport}
                setInitialViewport={setInitialViewport}
                setHovered={setHovered}
                setViewport={setViewport}
                handleSalonFocus={handleSalonFocus}
              />
            )}
          </div>
        )}
      </>
    </Layout>
  );
}

export async function getStaticProps({ locale }) {
  // return {
  //   notFound: true,
  // };
  await ensureServerAuth();
  const salonsResponse = await db.collection('salons').get();
  const fullSalonData = salonsResponse.docs.map(doc => doc.data());
  const salons = fullSalonData
    .filter(salon => salon.qualifiesForSalonFinder)
    .map(salon => ({
      position: salon.position,
      salonName: salon.salonName || null,
      salonNo: salon.salonNo || null,
      salonLoyalty: salon.salonLoyalty || false,
      client100: salon.exclusive ?? salon.client100 ?? false,
      AddressLine2: salon.AddressLine2 || null,
      ZipCode: salon.ZipCode || null,
      zipCode: salon.zipCode || null,
      city: salon.city || null,
      country: salon.country || null,
      phoneNumber: salon.phoneNumber || null,
      website: salon.website || null,
      salonEmail: salon.salonEmail || null,
      parentEmail: salon.parentEmail || null,
      '1922': salon['1922'] || false,
      keuneYou: salon.soPureColor ?? salon.keuneYou ?? false,
      SoPure: salon.soPureCare ?? salon.SoPure ?? false,
      blend: salon.semiColor ?? salon.blend ?? false,
      color: salon.tintaColor ?? salon.color ?? false,
      style: salon.style || false,
      care: salon.care || false,
    }));

  const globalModules = await getGlobal(reformatLocaleToContentfulLocale(locale));

  const { hasSalonFinder } = globalModules?.settings.locale;

  if (!hasSalonFinder) {
    return {
      notFound: true,
    };
  }

  return {
    props: { salonData: salons, globalModules, locale },
    revalidate: 10800,
  };
}
